import React, {useEffect, useState} from 'react';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling.ts';
import {
  Button,
  ButtonVariant,
  FormItem,
  InputType, useKeyboardShortcut,
  useModal,
  useToast
} from '@fs/uikit-react';
import styles from './SettingModal.module.css';
import {Setting, SettingsApi, SettingType} from '../../../SettingsApi.ts';

interface SettingModalProps {
  setting?: Setting; // Optional: if provided, we're in edit mode; if not, we're in create mode
  onSuccess: () => void;
}

const SettingModal: React.FC<SettingModalProps> = ({setting, onSuccess}) => {
  const {closeModal} = useModal();
  const toast = useToast();
  const isEditMode = !!setting;

  // State for form fields
  const [scope, setScope] = useState(setting?.scope || '');
  const [key, setKey] = useState(setting?.key || '');
  const [valueType, setValueType] = useState(setting?.valueType || SettingType.STRING);
  const [valueText, setValueText] = useState(setting?.valueText || '');
  const [description, setDescription] = useState(setting?.description || '');
  const [isLoading, setIsLoading] = useState(false);

  // Value type options
  const valueTypeOptions = [
    {value: SettingType.STRING, label: 'рядок'},
    {value: SettingType.NUMBER, label: 'число'},
    {value: SettingType.BOOLEAN, label: 'логічне значення'},
    {value: SettingType.SECRET, label: 'секрет'}
  ];

  // Update form fields when setting changes (only in edit mode)
  useEffect(() => {
    if (setting) {
      setScope(setting.scope);
      setKey(setting.key);
      setValueType(setting.valueType);
      setValueText(setting.valueText);
      setDescription(setting.description || '');
    }
  }, [setting]);

  // Handle form submission
  const handleSubmit = async () => {

    // Validate required fields
    if (!scope || !key || !valueType || !valueText) {
      toast.showErrorToast('Помилка валідації', 'Будь ласка, заповніть усі обов\'язкові поля');
      return;
    }

    setIsLoading(true);

    try {
      if (isEditMode && setting) {
        await SettingsApi.update(setting.id, {
          scope,
          key,
          valueType,
          valueText,
          description
        });

        toast.showSuccessToast('Успішно', 'Налаштування успішно оновлено');
      } else {
        await SettingsApi.create({
          scope,
          key,
          valueType,
          valueText,
          description
        });

        toast.showSuccessToast('Успішно', 'Налаштування успішно створено');
      }

      closeModal();
      onSuccess();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} setting:`, error);
      toast.showErrorToast(
        `Помилка ${isEditMode ? 'оновлення' : 'створення'} налаштування`,
        getUserFriendlyErrorMessage(error)
      );
    } finally {
      setIsLoading(false);
    }
  };

  useKeyboardShortcut('Enter', [], handleSubmit);

  return (
    <div className={styles.modalContent}>
      <FormItem
        label="Область"
        placeholder="введіть область"
        value={scope}
        onChange={(e) => { setScope(e.target.value); }}
        required
      />

      <FormItem
        label="Ключ"
        placeholder="введіть ключ"
        value={key}
        onChange={(e) => { setKey(e.target.value); }}
        required
      />

      <FormItem
        label="Тип значення"
        type={InputType.SELECT}
        options={valueTypeOptions}
        value={valueType}
        onChange={(e) => { setValueType(e.target.value); }}
        required
      />

      <FormItem
        label="Значення"
        placeholder="введіть значення"
        type={valueType === SettingType.SECRET ? InputType.PASSWORD : InputType.TEXT}
        typeSettings={valueType === SettingType.SECRET ? { showGeneratePasswordIcon: true } : {}}
        showCopyButton={valueType === SettingType.SECRET}
        value={valueText}
        onChange={(e) => { setValueText(e.target.value); }}
        required
      />

      <FormItem
        label="Опис"
        type={InputType.TEXT_AREA}
        placeholder="введіть опис"
        value={description}
        onChange={(e) => { setDescription(e.target.value); }}
      />

      <div className={styles.buttonGroup}>
        <Button
          title={isLoading
            ? (isEditMode ? 'Збереження...' : 'Створення...')
            : (isEditMode ? 'Зберегти' : 'Створити')}
          onClick={handleSubmit}
          loading={isLoading}
        />
        <Button
          title="Скасувати"
          onClick={closeModal}
          variant={ButtonVariant.SECONDARY}
        />
      </div>
    </div>
  );
};

export default SettingModal;
